import { useMount } from '@studio/react-utils/hooks';
import { useCallback, useRef, useState } from 'react';
import { isReserved } from '#~/hot-keys/index.js';

export const KONAMI_SEQUENCE = [
  'ArrowUp',
  'ArrowUp',
  'ArrowDown',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'ArrowLeft',
  'ArrowRight',
  'KeyB',
  'KeyA'
] as const;

const CODE_GLYPHS: Record<string, string> = {
  ArrowUp: '\u2191',
  ArrowDown: '\u2193',
  ArrowLeft: '\u2190',
  ArrowRight: '\u2192',
  KeyB: 'B',
  KeyA: 'A'
};

export const toGlyph = (code: string): string => CODE_GLYPHS[code] ?? code;

interface KonamiState {
  readonly position: number;
  readonly length: number;
}

export const useKonamiCode = (onUnlock: () => void): KonamiState => {
  const [position, setPosition] = useState(0);
  const positionRef = useRef(0);
  const onUnlockRef = useRef(onUnlock);
  onUnlockRef.current = onUnlock;

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (isReserved(event)) return;

    if (event.code === KONAMI_SEQUENCE[positionRef.current]) {
      const next = positionRef.current + 1;
      positionRef.current = next;
      setPosition(next);

      if (next === KONAMI_SEQUENCE.length) {
        positionRef.current = 0;
        onUnlockRef.current();
      }
    } else {
      const reset = event.code === KONAMI_SEQUENCE[0] ? 1 : 0;
      positionRef.current = reset;
      setPosition(reset);
    }
  }, []);

  useMount(() => {
    const abortController = new AbortController();

    document.addEventListener('keydown', handleKeyDown, { signal: abortController.signal });

    return () => {
      abortController.abort();
    };
  });

  return { position, length: KONAMI_SEQUENCE.length };
};
